import React, { useState, useEffect } from 'react';
import { usePlayer } from '../../context/PlayerContext';
import { useSeason } from '../../context/SeasonContext';
import { useMilestones } from '../../context/MilestoneContext';
import HeaderWithSeasonSelector from '../shared/HeaderWithSeasonSelector';
import { Save, Plus, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { convertBaseballInningsToDecimal } from '../../utils/calculations';

const AddGameForm = () => {
  const { addGame, playerData, updateProfile, activeSeason, viewingAllSeasons } = usePlayer();
  const { seasons } = useSeason();
  const { checkMilestones } = useMilestones();
  const [showAddLeague, setShowAddLeague] = useState(false);
  const [newLeagueName, setNewLeagueName] = useState('');
  const [seasonMessage, setSeasonMessage] = useState('');
  
  const [showAdvancedBattingStats, setShowAdvancedBattingStats] = useState(false);
  const [showAdvancedPitchingStats, setShowAdvancedPitchingStats] = useState(false);
  
  // Initialize gameData with the first league from player's leagues or empty string if none exist
  const [gameData, setGameData] = useState({
    date: new Date().toISOString().split('T')[0],
    opponent: '',
    league: playerData?.leagues?.length > 0 ? playerData.leagues[0] : '',
    seasonId: activeSeason ? activeSeason.id : '',
    // Bat information
    batMaker: '',
    batWoodType: '',
    batKnobType: 'Standard',
    batWeight: 30,
    batLength: 33,
    batModel: '',
    // Basic batting stats
    atBats: 0,
    hits: 0,
    doubles: 0,
    triples: 0,
    homeRuns: 0,
    runs: 0,
    rbi: 0,
    walks: 0,
    battingStrikeouts: 0,  // Renamed from strikeouts
    hitByPitch: 0,
    // Advanced batting stats
    sacrificeFlies: 0,
    sacrificeBunts: 0,
    stolenBases: 0,
    caughtStealing: 0,
    groundOuts: 0,
    flyOuts: 0,
    plateAppearances: 0,
    leftOnBase: 0,
    // Pitching stats
    didPitch: false,
    inningsPitched: '',
    pitchingStrikeouts: 0,  // Renamed from strikeouts
    hitsAllowed: 0,
    walksAllowed: 0,
    earnedRuns: 0,
    pitchingResult: 'No Decision',
    // Advanced pitching stats
    pitchCount: 0,
    strikes: 0,
    balls: 0,
    wildPitches: 0,
    hitBatters: 0,
    balks: 0,
    pickoffs: 0,
    groundBallOuts: 0,
    flyBallOuts: 0,
    completedGame: false,
    shutout: false
  });

  // Update league and season values if playerData or activeSeason changes
  useEffect(() => {
    if (playerData?.leagues?.length > 0 && !gameData.league) {
      setGameData(prevData => ({
        ...prevData,
        league: playerData.leagues[0]
      }));
    }
    
    if (activeSeason && !gameData.seasonId) {
      setGameData(prevData => ({
        ...prevData,
        seasonId: activeSeason.id
      }));
    }
  }, [playerData, gameData.league, activeSeason, gameData.seasonId]);

  // Update season message based on selected season
  useEffect(() => {
    if (viewingAllSeasons) {
      setSeasonMessage('Adding game without season association (All Seasons mode)');
    } else if (gameData.seasonId) {
      const selectedSeason = seasons?.find(s => s.id === gameData.seasonId);
      if (selectedSeason) {
        setSeasonMessage(`Adding game to: ${selectedSeason.name} (${selectedSeason.year})`);
      } else if (activeSeason) {
        setSeasonMessage(`Adding game to: ${activeSeason.name} (${activeSeason.year})`);
      } else {
        setSeasonMessage('Selected season not found');
      }
    } else if (seasons && seasons.length === 0) {
      setSeasonMessage('No seasons created yet. Create a season first to track season stats.');
    } else {
      setSeasonMessage('No season selected. Game will be added to career stats only.');
    }
  }, [gameData.seasonId, activeSeason, viewingAllSeasons, seasons]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setGameData({
      ...gameData,
      [name]: newValue
    });
  };

  const handleAddLeague = async () => {
    if (!newLeagueName.trim()) return;
    
    // Create a new array with the new league added
    const updatedLeagues = [...(playerData.leagues || []), newLeagueName.trim()];
    
    try {
      // Use updateProfile from the context to update the player data
      if (updateProfile) {
        await updateProfile({
          ...playerData,
          leagues: updatedLeagues
        });
        
        // Update the current form to use the new league
        setGameData({
          ...gameData,
          league: newLeagueName.trim()
        });
        
        // Reset the form
        setNewLeagueName('');
        setShowAddLeague(false);
      }
    } catch (error) {
      console.error("Error adding league:", error);
      alert("Failed to add league. Please try again.");
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    console.log("[GAME DEBUG] Starting game submission process");

    // Validate batting stats before submission
    const validationErrors = [];

    // Convert to numbers for validation
    const atBats = Number(gameData.atBats) || 0;
    const hits = Number(gameData.hits) || 0;
    const doubles = Number(gameData.doubles) || 0;
    const triples = Number(gameData.triples) || 0;
    const homeRuns = Number(gameData.homeRuns) || 0;
    const extraBaseHits = doubles + triples + homeRuns;

    // Critical validations
    if (hits > atBats) {
      validationErrors.push(`Hits (${hits}) cannot be greater than At-Bats (${atBats})`);
    }

    if (extraBaseHits > hits) {
      validationErrors.push(`Extra base hits (${extraBaseHits}) cannot be greater than total hits (${hits})`);
    }

    // Negative number checks
    if (atBats < 0) validationErrors.push('At-Bats cannot be negative');
    if (hits < 0) validationErrors.push('Hits cannot be negative');
    if (doubles < 0) validationErrors.push('Doubles cannot be negative');
    if (triples < 0) validationErrors.push('Triples cannot be negative');
    if (homeRuns < 0) validationErrors.push('Home Runs cannot be negative');
    if (Number(gameData.runs) < 0) validationErrors.push('Runs cannot be negative');
    if (Number(gameData.rbi) < 0) validationErrors.push('RBI cannot be negative');
    if (Number(gameData.walks) < 0) validationErrors.push('Walks cannot be negative');

    // Pitching validation if applicable
    if (gameData.didPitch) {
      const inningsPitched = Number(gameData.inningsPitched) || 0;
      const earnedRuns = Number(gameData.earnedRuns) || 0;
      const hitsAllowed = Number(gameData.hitsAllowed) || 0;
      const walksAllowed = Number(gameData.walksAllowed) || 0;
      const pitchingStrikeouts = Number(gameData.pitchingStrikeouts) || 0;

      if (inningsPitched < 0) validationErrors.push('Innings Pitched cannot be negative');
      if (earnedRuns < 0) validationErrors.push('Earned Runs cannot be negative');
      if (hitsAllowed < 0) validationErrors.push('Hits Allowed cannot be negative');
      if (walksAllowed < 0) validationErrors.push('Walks Allowed cannot be negative');
      if (pitchingStrikeouts < 0) validationErrors.push('Strikeouts cannot be negative');
    }

    // If there are validation errors, show them and stop submission
    if (validationErrors.length > 0) {
      alert('Please fix the following errors:\n\n' + validationErrors.join('\n'));
      console.error("[GAME DEBUG] Validation errors:", validationErrors);
      return;
    }

    const formattedGame = {
      date: new Date(gameData.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      opponent: gameData.opponent,
      league: gameData.league,
      // Add bat information
      batInfo: {
        maker: gameData.batMaker,
        woodType: gameData.batWoodType,
        knobType: gameData.batKnobType,
        weight: parseFloat(gameData.batWeight),
        length: parseFloat(gameData.batLength),
        model: gameData.batModel
      },
      battingStats: {
        // Basic batting stats
        atBats: gameData.atBats,
        hits: gameData.hits,
        doubles: gameData.doubles,
        triples: gameData.triples,
        homeRuns: gameData.homeRuns,
        runs: gameData.runs,
        rbi: gameData.rbi,
        walks: gameData.walks,
        strikeouts: gameData.battingStrikeouts,
        hitByPitch: gameData.hitByPitch,
        // Advanced batting stats (only include if they have values)
        ...(gameData.sacrificeFlies > 0 && { sacrificeFlies: gameData.sacrificeFlies }),
        ...(gameData.sacrificeBunts > 0 && { sacrificeBunts: gameData.sacrificeBunts }),
        ...(gameData.stolenBases > 0 && { stolenBases: gameData.stolenBases }),
        ...(gameData.caughtStealing > 0 && { caughtStealing: gameData.caughtStealing }),
        ...(gameData.groundOuts > 0 && { groundOuts: gameData.groundOuts }),
        ...(gameData.flyOuts > 0 && { flyOuts: gameData.flyOuts }),
        ...(gameData.plateAppearances > 0 && { plateAppearances: gameData.plateAppearances }),
        ...(gameData.leftOnBase > 0 && { leftOnBase: gameData.leftOnBase })
      }
    };
    
    // Log batting stats for debugging
    console.log("[GAME DEBUG] Formatted game batting stats:", {
      atBats: formattedGame.battingStats.atBats,
      hits: formattedGame.battingStats.hits,
      doubles: formattedGame.battingStats.doubles,
      triples: formattedGame.battingStats.triples,
      homeRuns: formattedGame.battingStats.homeRuns
    });
    
    // Add pitching stats if applicable
    if (gameData.didPitch) {
      formattedGame.pitchingStats = {
        // Basic pitching stats
        inningsPitched: convertBaseballInningsToDecimal(gameData.inningsPitched),
        strikeouts: gameData.pitchingStrikeouts,
        hits: gameData.hitsAllowed,
        walks: gameData.walksAllowed,
        earnedRuns: gameData.earnedRuns,
        result: gameData.pitchingResult,
        // Advanced pitching stats (only include if they have values)
        ...(gameData.pitchCount > 0 && { pitchCount: gameData.pitchCount }),
        ...(gameData.strikes > 0 && { strikes: gameData.strikes }),
        ...(gameData.balls > 0 && { balls: gameData.balls }),
        ...(gameData.wildPitches > 0 && { wildPitches: gameData.wildPitches }),
        ...(gameData.hitBatters > 0 && { hitBatters: gameData.hitBatters }),
        ...(gameData.balks > 0 && { balks: gameData.balks }),
        ...(gameData.pickoffs > 0 && { pickoffs: gameData.pickoffs }),
        ...(gameData.groundBallOuts > 0 && { groundBallOuts: gameData.groundBallOuts }),
        ...(gameData.flyBallOuts > 0 && { flyBallOuts: gameData.flyBallOuts }),
        ...(gameData.completedGame && { completedGame: gameData.completedGame }),
        ...(gameData.shutout && { shutout: gameData.shutout })
      };
    }
    
    // Save the game
    if (gameData.seasonId) {
      // Associate the game with the selected season
      formattedGame.seasonId = gameData.seasonId;
      
      // Find the season name for the alert message
      const selectedSeason = seasons?.find(s => s.id === gameData.seasonId);
      const seasonName = selectedSeason ? `${selectedSeason.name} (${selectedSeason.year})` : 'selected';
      
      console.log(`[GAME DEBUG] Adding game to season: ${gameData.seasonId}`);
      
      try {
        // Wait for the game to be added
        await addGame(formattedGame);
        console.log("[GAME DEBUG] Game added successfully to database");
        
        // Wait for milestone checks to complete
        console.log("[GAME DEBUG] Checking milestones after game addition");
        await checkMilestones(formattedGame);

        alert(`Game added successfully to ${seasonName} season!`);
      } catch (error) {
        console.error("[GAME DEBUG] Error adding game:", error);
        alert(`Error adding game: ${error.message}`);
      }
    } else {
      console.log("[GAME DEBUG] Adding game to career stats (no season)");
      
      try {
        // Wait for the game to be added
        await addGame(formattedGame);
        console.log("[GAME DEBUG] Game added successfully to database");
        
        // Wait for milestone checks to complete
        console.log("[GAME DEBUG] Checking milestones after game addition");
        await checkMilestones(formattedGame);

        alert("Game added successfully to career stats!");
      } catch (error) {
        console.error("[GAME DEBUG] Error adding game:", error);
        alert(`Error adding game: ${error.message}`);
      }
    }
    
    // Reset the form
    setGameData({
      date: new Date().toISOString().split('T')[0],
      opponent: '',
      league: playerData?.leagues?.length > 0 ? playerData.leagues[0] : '',
      seasonId: activeSeason ? activeSeason.id : '',
      // Bat information
      batMaker: '',
      batWoodType: '',
      batKnobType: 'Standard',
      batWeight: 30,
      batLength: 33,
      batModel: '',
      // Basic batting stats
      atBats: 0,
      hits: 0,
      doubles: 0,
      triples: 0,
      homeRuns: 0,
      runs: 0,
      rbi: 0,
      walks: 0,
      battingStrikeouts: 0,
      hitByPitch: 0,
      // Advanced batting stats
      sacrificeFlies: 0,
      sacrificeBunts: 0,
      stolenBases: 0,
      caughtStealing: 0,
      groundOuts: 0,
      flyOuts: 0,
      plateAppearances: 0,
      leftOnBase: 0,
      // Pitching stats
      didPitch: false,
      inningsPitched: '',
      pitchingStrikeouts: 0,
      hitsAllowed: 0,
      walksAllowed: 0,
      earnedRuns: 0,
      pitchingResult: 'No Decision',
      // Advanced pitching stats
      pitchCount: 0,
      strikes: 0,
      balls: 0,
      wildPitches: 0,
      hitBatters: 0,
      balks: 0,
      pickoffs: 0,
      groundBallOuts: 0,
      flyBallOuts: 0,
      completedGame: false,
      shutout: false
    });
    
    // Reset advanced stats toggles
    setShowAdvancedBattingStats(false);
    setShowAdvancedPitchingStats(false);
  };
  
  return (
    <div className="pb-24 dark:bg-dark-100">
      <HeaderWithSeasonSelector 
        title="Add New Game"
        subtitle="Record your game statistics"
        actions={[
          { 
            icon: Save, 
            onClick: handleSubmit, 
            label: 'Save Game'
          }
        ]}
      />
      
      <div className="p-4">
      
      {/* Season information banner */}
      {seasonMessage && (
        <div className={`mb-4 p-3 flex items-center rounded-md border ${
          !activeSeason && !viewingAllSeasons 
            ? 'border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 text-amber-700 dark:text-amber-300' 
            : viewingAllSeasons
            ? 'border-purple-200 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-800 text-purple-700 dark:text-purple-300'
            : 'border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800 text-blue-700 dark:text-blue-300'
        }`}>
          <Calendar size={16} className="mr-2 flex-shrink-0" />
          <span className="text-sm">{seasonMessage}</span>
        </div>
      )}
      
      <form className="space-y-4" onSubmit={handleSubmit}>
        {/* Game Info Section */}
        <div className="grid grid-cols-2 gap-4">
          {/* Date Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
              Date
            </label>
            <input 
              type="date" 
              className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
              name="date"
              value={gameData.date}
              onChange={handleChange}
              required
            />
          </div>
          
          {/* Opponent Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
              Opponent
            </label>
            <input 
              type="text" 
              className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
              placeholder="Team name"
              name="opponent"
              value={gameData.opponent}
              onChange={handleChange}
              required
            />
          </div>
        </div>
        
        {/* League and Season Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* League Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
              League
            </label>
            <div className="flex gap-2">
              <select 
                className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
                name="league"
                value={gameData.league}
                onChange={handleChange}
              >
                {/* Map through player's leagues or show a default if none exist */}
                {playerData?.leagues?.length > 0 ? (
                  playerData.leagues.map((league, index) => (
                    <option key={index} value={league}>{league}</option>
                  ))
                ) : (
                  <option value="">No leagues available</option>
                )}
              </select>
              
              <button 
                type="button" 
                className="bg-gray-200 dark:bg-dark-400 text-gray-700 dark:text-darkText-primary p-2 rounded-md hover:bg-gray-300 dark:hover:bg-dark-300 flex items-center justify-center"
                onClick={() => setShowAddLeague(!showAddLeague)}
              >
                <Plus size={20} />
              </button>
            </div>
            
            {/* Add new league input */}
            {showAddLeague && (
              <div className="mt-2 flex gap-2 dark:bg-dark-300 rounded-md">
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
                  placeholder="Enter new league name"
                  value={newLeagueName}
                  onChange={(e) => setNewLeagueName(e.target.value)}
                />
                <button
                  type="button"
                  className="bg-blue-600 dark:bg-blue-700 text-white py-2 px-4 rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                  onClick={handleAddLeague}
                >
                  Add
                </button>
              </div>
            )}
          </div>
          
          {/* Season Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
              Season
            </label>
            <select 
              className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
              name="seasonId"
              value={gameData.seasonId}
              onChange={handleChange}
            >
              <option value="">No Season (Career Stats Only)</option>
              {seasons && seasons.length > 0 ? (
                seasons.map((season) => (
                  <option key={season.id} value={season.id}>
                    {season.name} ({season.year})
                  </option>
                ))
              ) : (
                <option value="" disabled>No seasons available</option>
              )}
            </select>
          </div>
        </div>
        
        {/* Bat Information Section */}
        <div className="border-b border-gray-200 dark:border-dark-400 pb-4">
          <h3 className="text-lg font-medium dark:text-darkText-primary mb-3">Bat Information</h3>
          
          <div className="grid grid-cols-2 gap-4 mb-3">
            {/* Bat Maker */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
                Bat Maker
              </label>
              <select 
                className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
                name="batMaker"
                value={gameData.batMaker}
                onChange={handleChange}
              >
                <option value="">Select Bat Maker</option>
                <option value="Marucci">Marucci</option>
                <option value="Victus">Victus</option>
                <option value="Louisville Slugger">Louisville Slugger</option>
                <option value="Mizuno">Mizuno</option>
                <option value="Rawlings">Rawlings</option>
                <option value="DeMarini">DeMarini</option>
                <option value="Old Hickory">Old Hickory</option>
                <option value="Beaver Bats">Beaver Bats</option>
                <option value="Warstic">Warstic</option>
                <option value="Nike">Nike</option>
                <option value="Other">Other</option>
              </select>
            </div>
            
            {/* Wood Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
                Wood Type
              </label>
              <select 
                className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
                name="batWoodType"
                value={gameData.batWoodType}
                onChange={handleChange}
              >
                <option value="">Select Wood Type</option>
                <option value="Maple">Maple</option>
                <option value="Hickory">Hickory</option>
                <option value="Bamboo">Bamboo</option>
                <option value="Ash">Ash</option>
                <option value="Birch">Birch</option>
                <option value="Composite">Composite</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-3">
            {/* Knob Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
                Knob Type
              </label>
              <select 
                className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
                name="batKnobType"
                value={gameData.batKnobType}
                onChange={handleChange}
              >
                <option value="Standard">Standard</option>
                <option value="Flared">Flared</option>
                <option value="Axe">Axe</option>
                <option value="Thin">Thin</option>
              </select>
            </div>
            
            {/* Bat Model */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
                Bat Model (Optional)
              </label>
              <input 
                type="text" 
                className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
                placeholder="e.g. AP5, 271, etc."
                name="batModel"
                value={gameData.batModel}
                onChange={handleChange}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Bat Length */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
                Bat Length: {gameData.batLength} inches
              </label>
              <input 
                type="range" 
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-dark-400"
                min="20"
                max="40"
                step="0.5"
                name="batLength"
                value={gameData.batLength}
                onChange={handleChange}
              />
              <div className="flex justify-between text-xs text-gray-500 dark:text-darkText-tertiary mt-1">
                <span>20"</span>
                <span>30"</span>
                <span>40"</span>
              </div>
            </div>
            
            {/* Bat Weight */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
                Bat Weight: {gameData.batWeight} oz
              </label>
              <input 
                type="range" 
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-dark-400"
                min="20"
                max="45"
                step="0.5"
                name="batWeight"
                value={gameData.batWeight}
                onChange={handleChange}
              />
              <div className="flex justify-between text-xs text-gray-500 dark:text-darkText-tertiary mt-1">
                <span>20oz</span>
                <span>32.5oz</span>
                <span>45oz</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Batting Stats Section */}
        <div className="border-b border-gray-200 dark:border-dark-400 pb-2">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-medium dark:text-darkText-primary">Batting Stats</h3>
            <button 
              type="button"
              onClick={() => setShowAdvancedBattingStats(!showAdvancedBattingStats)}
              className="text-blue-600 dark:text-blue-400 text-sm flex items-center"
            >
              {showAdvancedBattingStats ? 'Hide Advanced Stats' : 'Show Advanced Stats'}
              {showAdvancedBattingStats ? <ChevronUp size={16} className="ml-1" /> : <ChevronDown size={16} className="ml-1" />}
            </button>
          </div>
          
          {/* Basic batting stats inputs */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">At Bats</label>
              <input 
                type="number" 
                className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                min="0"
                name="atBats"
                value={gameData.atBats}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Hits</label>
              <input 
                type="number" 
                className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                min="0"
                name="hits"
                value={gameData.hits}
                onChange={handleChange}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4 mt-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">2B</label>
              <input 
                type="number" 
                className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                min="0"
                name="doubles"
                value={gameData.doubles}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">3B</label>
              <input 
                type="number" 
                className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                min="0"
                name="triples"
                value={gameData.triples}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">HR</label>
              <input 
                type="number" 
                className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                min="0"
                name="homeRuns"
                value={gameData.homeRuns}
                onChange={handleChange}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4 mt-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Runs</label>
              <input 
                type="number" 
                className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                min="0"
                name="runs"
                value={gameData.runs}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">RBI</label>
              <input 
                type="number" 
                className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                min="0"
                name="rbi"
                value={gameData.rbi}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">BB</label>
              <input 
                type="number" 
                className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                min="0"
                name="walks"
                value={gameData.walks}
                onChange={handleChange}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mt-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">K</label>
              <input 
                type="number" 
                className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                min="0"
                name="battingStrikeouts"
                value={gameData.battingStrikeouts}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">HBP</label>
              <input 
                type="number" 
                className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                min="0"
                name="hitByPitch"
                value={gameData.hitByPitch}
                onChange={handleChange}
              />
            </div>
          </div>
          
          {/* Advanced batting stats */}
          {showAdvancedBattingStats && (
            <div className="mt-4 pt-2 border-t border-gray-200 dark:border-dark-400">
              <h4 className="text-md font-medium dark:text-darkText-primary mb-2">Advanced Batting Stats</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Sacrifice Flies</label>
                  <input 
                    type="number" 
                    className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                    min="0"
                    name="sacrificeFlies"
                    value={gameData.sacrificeFlies}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Sacrifice Bunts</label>
                  <input 
                    type="number" 
                    className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                    min="0"
                    name="sacrificeBunts"
                    value={gameData.sacrificeBunts}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Stolen Bases</label>
                  <input 
                    type="number" 
                    className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                    min="0"
                    name="stolenBases"
                    value={gameData.stolenBases}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Caught Stealing</label>
                  <input 
                    type="number" 
                    className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                    min="0"
                    name="caughtStealing"
                    value={gameData.caughtStealing}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Ground Outs</label>
                  <input 
                    type="number" 
                    className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                    min="0"
                    name="groundOuts"
                    value={gameData.groundOuts}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Fly Outs</label>
                  <input 
                    type="number" 
                    className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                    min="0"
                    name="flyOuts"
                    value={gameData.flyOuts}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Plate Appearances</label>
                  <input 
                    type="number" 
                    className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                    min="0"
                    name="plateAppearances"
                    value={gameData.plateAppearances}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Left On Base</label>
                  <input 
                    type="number" 
                    className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                    min="0"
                    name="leftOnBase"
                    value={gameData.leftOnBase}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Pitching Stats Section */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center">
              <h3 className="text-lg font-medium dark:text-darkText-primary">Pitching Stats</h3>
              <div className="ml-2">
                <input 
                  type="checkbox" 
                  id="didPitch" 
                  className="mr-1 dark:bg-dark-300 dark:border-dark-400"
                  name="didPitch"
                  checked={gameData.didPitch}
                  onChange={handleChange}
                />
                <label htmlFor="didPitch" className="text-sm dark:text-darkText-primary">I pitched in this game</label>
              </div>
            </div>
            {gameData.didPitch && (
              <button 
                type="button"
                onClick={() => setShowAdvancedPitchingStats(!showAdvancedPitchingStats)}
                className="text-blue-600 dark:text-blue-400 text-sm flex items-center"
              >
                {showAdvancedPitchingStats ? 'Hide Advanced Stats' : 'Show Advanced Stats'}
                {showAdvancedPitchingStats ? <ChevronUp size={16} className="ml-1" /> : <ChevronDown size={16} className="ml-1" />}
              </button>
            )}
          </div>
          
          {/* Pitching stats inputs */}
          {gameData.didPitch && (
            <div className="space-y-4 mt-2">
              {/* Basic Pitching Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Innings Pitched</label>
                  <input 
                    type="text" 
                    className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
                    placeholder="e.g. 6.2 for 6⅔ innings"
                    name="inningsPitched"
                    value={gameData.inningsPitched}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Strikeouts</label>
                  <input 
                    type="number" 
                    className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                    min="0"
                    name="pitchingStrikeouts"
                    value={gameData.pitchingStrikeouts}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Hits Allowed</label>
                  <input 
                    type="number" 
                    className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                    min="0"
                    name="hitsAllowed"
                    value={gameData.hitsAllowed}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Walks Allowed</label>
                  <input 
                    type="number" 
                    className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                    min="0"
                    name="walksAllowed"
                    value={gameData.walksAllowed}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Earned Runs</label>
                  <input 
                    type="number" 
                    className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                    min="0"
                    name="earnedRuns"
                    value={gameData.earnedRuns}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Result</label>
                <select 
                  className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
                  name="pitchingResult"
                  value={gameData.pitchingResult}
                  onChange={handleChange}
                >
                  <option value="No Decision">No Decision</option>
                  <option value="Win">Win</option>
                  <option value="Loss">Loss</option>
                  <option value="Save">Save</option>
                  <option value="Hold">Hold</option>
                  <option value="Blown Save">Blown Save</option>
                </select>
              </div>
              
              {/* Advanced Pitching Stats */}
              {showAdvancedPitchingStats && (
                <div className="mt-4 pt-2 border-t border-gray-200 dark:border-dark-400">
                  <h4 className="text-md font-medium dark:text-darkText-primary mb-2">Advanced Pitching Stats</h4>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Pitch Count</label>
                      <input 
                        type="number" 
                        className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                        min="0"
                        name="pitchCount"
                        value={gameData.pitchCount}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Strikes</label>
                      <input 
                        type="number" 
                        className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                        min="0"
                        name="strikes"
                        value={gameData.strikes}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Balls</label>
                      <input 
                        type="number" 
                        className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                        min="0"
                        name="balls"
                        value={gameData.balls}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Wild Pitches</label>
                      <input 
                        type="number" 
                        className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                        min="0"
                        name="wildPitches"
                        value={gameData.wildPitches}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Hit Batters</label>
                      <input 
                        type="number" 
                        className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                        min="0"
                        name="hitBatters"
                        value={gameData.hitBatters}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Balks</label>
                      <input 
                        type="number" 
                        className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                        min="0"
                        name="balks"
                        value={gameData.balks}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Pickoffs</label>
                      <input 
                        type="number" 
                        className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                        min="0"
                        name="pickoffs"
                        value={gameData.pickoffs}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Ground Ball Outs</label>
                      <input 
                        type="number" 
                        className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                        min="0"
                        name="groundBallOuts"
                        value={gameData.groundBallOuts}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Fly Ball Outs</label>
                      <input 
                        type="number" 
                        className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                        min="0"
                        name="flyBallOuts"
                        value={gameData.flyBallOuts}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="completedGame" 
                        className="mr-2 dark:bg-dark-300 dark:border-dark-400"
                        name="completedGame"
                        checked={gameData.completedGame}
                        onChange={handleChange}
                      />
                      <label htmlFor="completedGame" className="text-sm dark:text-darkText-primary">Completed Game</label>
                    </div>
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="shutout" 
                        className="mr-2 dark:bg-dark-300 dark:border-dark-400"
                        name="shutout"
                        checked={gameData.shutout}
                        onChange={handleChange}
                      />
                      <label htmlFor="shutout" className="text-sm dark:text-darkText-primary">Shutout</label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Submit Button */}
        <div className="mt-6 flex justify-end">
          <button
            type="submit"
            className="bg-blue-600 dark:bg-blue-700 text-white py-2 px-6 rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 flex items-center"
          >
            <Save size={18} className="mr-2" />
            Save Game
          </button>
        </div>
      </form>
      </div>
    </div>
  );
};

export default AddGameForm;
